// src/pages/EventsLanding.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';

interface EventCard {
  id: string;
  title: string;
  startDate: Date;
  location: string;
  imageUrl: string;
  expectedGuests: number;
  deleted:boolean;
}

export default function EventsLanding() {
  const [events, setEvents] = useState<EventCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = collection(db, 'events');
        const q = query(
          eventsRef,
          where('deleted', '==', 0),
          orderBy('startDate', 'desc') // Sort latest first
        );
        const querySnapshot = await getDocs(q);
  
        const eventsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title?.trim() || 'Untitled Event',
            startDate: data.startDate?.toDate?.() ?? new Date(),
            location: data.eventAddress || 'Location not specified',
            imageUrl: data.invitationFileURL || 'https://placehold.co/600x400?text=Event+Image',
            expectedGuests: data.expectedGuests || 0
          } as EventCard;
        });
  
        setEvents(eventsData);
      } catch (err) {
        setError('Failed to load events');
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchEvents();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <ArrowPathIcon className="w-12 h-12 text-[#0c8842] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center p-8 max-w-md bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Events</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#0c8842] text-white px-6 py-2 rounded-lg hover:bg-green-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Upcoming Events & Celebrations
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover memorable events and celebrations. Find your next experience to cherish.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              <Link to={`/apps/eventiq/event/${event.id}`} className="block relative group">
                <img
                  src={event.imageUrl}
                  alt={event.title}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity" />
              </Link>
              
              <div className="p-6">
                <div className="mb-4">
                  <Link to={`/apps/eventiq/event/${event.id}`} className="hover:underline">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                      {event.title}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <CalendarDaysIcon className="w-5 h-5 mr-2 text-blue-500" />
                    <span>
                      {event.startDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <MapPinIcon className="w-5 h-5 mr-2 text-blue-500" />
                    <span className="line-clamp-2">{event.location}</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">👥</span>
                    <span>{event.expectedGuests} expected guests</span>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t pt-4">
                  <Link
                    to={`/apps/eventiq/event/${event.id}`}
                    className="bg-[#0c8842] text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors text-sm font-medium flex items-center"
                  >
                    <span>View Details</span>
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {events.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">
              No upcoming events found
            </div>
            <div className="text-gray-400 text-sm">
              Check back later for new events!
            </div>
          </div>
        )}
      </div>
    </div>
  );
}